import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Users, Plus, Edit2, Trash2, Shield, FileText, Image, 
  TrendingUp, Settings, CheckCircle, Clock, Eye,
  Save, X, Loader2, Wand2, BookOpen, BarChart2, ArrowLeft, PenLine
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';

const TECH_FONT = "'Inter', 'SF Pro Display', sans-serif";

const STAFF_ROLES = {
  admin: { name: "Super Administrador", color: "bg-red-100 text-red-800", icon: Shield },
  editor: { name: "Editor", color: "bg-blue-100 text-blue-800", icon: FileText }
};

// `embedded`: renderiza solo el contenido (sin header de página) para usarla
// dentro de un tab del panel editorial.
const StaffManagementPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('staff');
  const [staffMembers, setStaffMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [drafts, setDrafts] = useState({ dossiers: [], editions: [] });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Modals
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [showEditStaff, setShowEditStaff] = useState(null);
  const [showGenerateContent, setShowGenerateContent] = useState(false);
  const [showCreateEdition, setShowCreateEdition] = useState(false);
  
  // Form states
  const [newStaff, setNewStaff] = useState({ email: '', password: '', name: '', staff_role: 'editor' });
  const [generateForm, setGenerateForm] = useState({ topic: '', category: 'política', content_type: 'dossier', generate_image: true });
  const [editionForm, setEditionForm] = useState({ title: '', dossier_ids: [], generate_cover: true });
  const [selectedDossiers, setSelectedDossiers] = useState([]);
  const [availableDossiers, setAvailableDossiers] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [staffData, usersData, draftsData, dossiersData] = await Promise.all([
        api.getStaffMembers(),
        api.getAllUsers(),
        api.getDraftContent(),
        api.getDossiers()
      ]);
      setStaffMembers(staffData || []);
      setAllUsers(usersData || []);
      setDrafts(draftsData || { dossiers: [], editions: [] });
      setAvailableDossiers(dossiersData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      await api.createStaffMember(newStaff);
      setShowCreateStaff(false);
      setNewStaff({ email: '', password: '', name: '', staff_role: 'editor' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleUpdateStaff = async (userId, data) => {
    try {
      await api.updateStaffMember(userId, data);
      setShowEditStaff(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleDeleteStaff = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      await api.deleteStaffMember(userId);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleGenerateContent = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const result = await api.generateDossier(generateForm);
      alert(`Dossier "${result.dossier?.title}" generado exitosamente`);
      setShowGenerateContent(false);
      setGenerateForm({ topic: '', category: 'política', content_type: 'dossier', generate_image: true });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al generar contenido');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateEdition = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const result = await api.generateEdition({
        title: editionForm.title || undefined,
        dossier_ids: selectedDossiers,
        generate_cover: editionForm.generate_cover
      });
      alert(`Edición "${result.edition?.title}" creada exitosamente`);
      setShowCreateEdition(false);
      setEditionForm({ title: '', dossier_ids: [], generate_cover: true });
      setSelectedDossiers([]);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear edición');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublishContent = async (type, id) => {
    try {
      await api.publishContent(type, id);
      alert('Contenido publicado exitosamente');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al publicar');
    }
  };

  const toggleDossierSelection = (dossierId) => {
    setSelectedDossiers(prev => 
      prev.includes(dossierId) 
        ? prev.filter(id => id !== dossierId)
        : [...prev, dossierId]
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'} data-testid="staff-management-page">
      {/* Header - Tech Style (solo como página independiente) */}
      {!embedded && (
        <div className="bg-[#0f172a] text-white py-6">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-4 mb-2">
                  <Link to="/admin" className="text-gray-400 hover:text-cyan-400 transition-colors">
                    <ArrowLeft className="w-5 h-5" />
                  </Link>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: TECH_FONT }}>
                    Gestión de Equipo
                  </h1>
                </div>
                <p className="text-gray-400 text-sm ml-9">Usuarios, roles y generación de contenido</p>
              </div>
              <Button
                onClick={() => navigate('/admin/write')}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="write-article-btn"
              >
                <PenLine className="w-4 h-4 mr-2" />
                Escribir Noticia
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-8'}>
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm p-4 space-y-1">
              {[
                { id: 'staff', icon: Users, label: 'Equipo' },
                { id: 'users', icon: Shield, label: 'Usuarios' },
                { id: 'generate', icon: Wand2, label: 'Generar Contenido' },
                { id: 'drafts', icon: Clock, label: 'Borradores', badge: (drafts.dossiers?.length || 0) + (drafts.editions?.length || 0) },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                      activeTab === item.id ? 'bg-[#0f172a] text-white' : 'hover:bg-slate-100 text-gray-700'
                    }`}
                    data-testid={`staff-nav-${item.id}`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                    {item.badge > 0 && (
                      <Badge className="bg-cyan-500 text-white ml-auto">{item.badge}</Badge>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Staff Tab */}
            {activeTab === 'staff' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: TECH_FONT }}>
                    Equipo Editorial
                  </h2>
                  <Button 
                    onClick={() => setShowCreateStaff(true)} 
                    className="bg-[#0f172a] hover:bg-slate-800"
                    data-testid="add-staff-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Agregar Miembro
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Usuario</th>
                          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Rol</th>
                          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Estado</th>
                          <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {staffMembers.map((member) => {
                          const role = STAFF_ROLES[member.staff_role] || STAFF_ROLES.editor;
                          const RoleIcon = role.icon;
                          return (
                            <tr key={member.user_id} className="hover:bg-slate-100 transition-colors">
                              <td className="px-6 py-4">
                                <div>
                                  <p className="font-medium text-gray-900">{member.name}</p>
                                  <p className="text-sm text-gray-500">{member.email}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <Badge className={role.color}>
                                  <RoleIcon className="w-3 h-3 mr-1" />
                                  {role.name}
                                </Badge>
                              </td>
                              <td className="px-6 py-4">
                                <Badge className={member.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                  {member.is_active !== false ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <Button variant="ghost" size="sm" onClick={() => setShowEditStaff(member)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-slate-600" onClick={() => handleDeleteStaff(member.user_id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {staffMembers.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        No hay miembros del equipo
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: TECH_FONT }}>
                  Todos los Usuarios
                </h2>
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Usuario</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Suscripción</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Registro</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {allUsers.map((u) => (
                        <tr key={u.user_id} className="hover:bg-slate-100 transition-colors">
                          <td className="px-6 py-4">
                            <p className="font-medium text-gray-900">{u.name}</p>
                            <p className="text-sm text-gray-500">{u.email}</p>
                          </td>
                          <td className="px-6 py-4">
                            <Badge className={u.user_type === 'admin' ? 'bg-red-100 text-red-800' : u.user_type === 'staff' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                              {u.user_type}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <Badge className={u.subscription_plan === 'premium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                              {u.subscription_plan || 'free'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('es') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {allUsers.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No hay usuarios registrados
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Generate Content Tab */}
            {activeTab === 'generate' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: TECH_FONT }}>
                  Generar Contenido con IA
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Generate Dossier */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Generar Noticia/Dossier</h3>
                        <p className="text-sm text-gray-500">Crea un artículo completo con IA</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setShowGenerateContent(true)} 
                      className="w-full bg-[#0f172a] hover:bg-slate-800"
                      data-testid="generate-dossier-btn"
                    >
                      <Wand2 className="w-4 h-4 mr-2" /> Generar Dossier
                    </Button>
                  </div>

                  {/* Create Edition */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Crear Edición Semanal</h3>
                        <p className="text-sm text-gray-500">Compila artículos en una edición</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setShowCreateEdition(true)} 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="create-edition-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Crear Edición
                    </Button>
                  </div>

                  {/* Add Influence Entry */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Índice de Influencia</h3>
                        <p className="text-sm text-gray-500">Agrega entidades al ranking</p>
                      </div>
                    </div>
                    <Button onClick={() => navigate('/admin')} variant="outline" className="w-full">
                      <BarChart2 className="w-4 h-4 mr-2" /> Ir al Panel
                    </Button>
                  </div>

                  {/* Generate Cover */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                        <Image className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Portadas e Imágenes</h3>
                        <p className="text-sm text-gray-500">Las imágenes se generan automáticamente</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      Al crear dossiers o ediciones, las portadas se generan con Gemini Nano Banana Pro.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Drafts Tab */}
            {activeTab === 'drafts' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: TECH_FONT }}>
                  Contenido Pendiente de Publicar
                </h2>
                
                {/* Draft Dossiers */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Dossiers en Borrador ({drafts.dossiers?.length || 0})
                  </h3>
                  {drafts.dossiers?.length > 0 ? (
                    <div className="space-y-3">
                      {drafts.dossiers.map((d) => (
                        <div key={d.dossier_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{d.title}</p>
                            <p className="text-sm text-gray-500">{d.sector} • {new Date(d.created_at).toLocaleDateString('es')}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/dossiers/${d.dossier_id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handlePublishContent('dossier', d.dossier_id)}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Publicar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No hay dossiers en borrador</p>
                  )}
                </div>

                {/* Draft Editions */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Ediciones en Borrador ({drafts.editions?.length || 0})
                  </h3>
                  {drafts.editions?.length > 0 ? (
                    <div className="space-y-3">
                      {drafts.editions.map((e) => (
                        <div key={e.edition_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{e.title}</p>
                            <p className="text-sm text-gray-500">Semana {e.week_number} • {e.sections?.length || 0} secciones</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handlePublishContent('edition', e.edition_id)}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Publicar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No hay ediciones en borrador</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Staff Modal */}
      <Dialog open={showCreateStaff} onOpenChange={setShowCreateStaff}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Miembro del Equipo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateStaff} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nombre</label>
              <Input 
                value={newStaff.name} 
                onChange={(e) => setNewStaff({...newStaff, name: e.target.value})}
                required
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input 
                type="email"
                value={newStaff.email} 
                onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                required
                data-testid="staff-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contraseña</label>
              <Input 
                type="password"
                value={newStaff.password} 
                onChange={(e) => setNewStaff({...newStaff, password: e.target.value})}
                required
                data-testid="staff-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rol</label>
              <Select value={newStaff.staff_role} onValueChange={(v) => setNewStaff({...newStaff, staff_role: v})}>
                <SelectTrigger data-testid="staff-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STAFF_ROLES).map(([key, role]) => (
                    <SelectItem key={key} value={key}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateStaff(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#0f172a] hover:bg-slate-800" data-testid="submit-staff-btn">
                Crear
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Modal */}
      <Dialog open={!!showEditStaff} onOpenChange={() => setShowEditStaff(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Miembro</DialogTitle>
          </DialogHeader>
          {showEditStaff && (
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateStaff(showEditStaff.user_id, { name: showEditStaff.name, staff_role: showEditStaff.staff_role, is_active: showEditStaff.is_active }); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre</label>
                <Input 
                  value={showEditStaff.name} 
                  onChange={(e) => setShowEditStaff({...showEditStaff, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rol</label>
                <Select value={showEditStaff.staff_role} onValueChange={(v) => setShowEditStaff({...showEditStaff, staff_role: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STAFF_ROLES).map(([key, role]) => (
                      <SelectItem key={key} value={key}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="is_active"
                  checked={showEditStaff.is_active !== false}
                  onChange={(e) => setShowEditStaff({...showEditStaff, is_active: e.target.checked})}
                />
                <label htmlFor="is_active" className="text-sm">Usuario activo</label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditStaff(null)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#0f172a] hover:bg-slate-800">
                  Guardar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Content Modal */}
      <Dialog open={showGenerateContent} onOpenChange={setShowGenerateContent}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generar Dossier con IA</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateContent} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tema o Título</label>
              <Textarea 
                value={generateForm.topic} 
                onChange={(e) => setGenerateForm({...generateForm, topic: e.target.value})}
                placeholder="Ej: Impacto de las nuevas políticas fiscales en la economía..."
                rows={3}
                required
                data-testid="generate-topic-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Categoría</label>
              <Select value={generateForm.category} onValueChange={(v) => setGenerateForm({...generateForm, category: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="política">Política</SelectItem>
                  <SelectItem value="economía">Economía</SelectItem>
                  <SelectItem value="negocios">Negocios</SelectItem>
                  <SelectItem value="actualidad">Actualidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="generate_image"
                checked={generateForm.generate_image}
                onChange={(e) => setGenerateForm({...generateForm, generate_image: e.target.checked})}
              />
              <label htmlFor="generate_image" className="text-sm">Generar imagen de portada</label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowGenerateContent(false)} className="flex-1" disabled={generating}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#0f172a] hover:bg-slate-800" disabled={generating} data-testid="submit-generate-btn">
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</> : <><Wand2 className="w-4 h-4 mr-2" /> Generar</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Edition Modal */}
      <Dialog open={showCreateEdition} onOpenChange={setShowCreateEdition}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Edición Semanal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEdition} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Título (opcional - se genera automáticamente)</label>
              <Input 
                value={editionForm.title} 
                onChange={(e) => setEditionForm({...editionForm, title: e.target.value})}
                placeholder="Ej: La Semana de los Cambios"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Seleccionar Dossiers ({selectedDossiers.length} seleccionados)</label>
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {availableDossiers.slice(0, 20).map((d) => (
                  <div 
                    key={d.dossier_id}
                    className={`p-3 border-b cursor-pointer transition ${selectedDossiers.includes(d.dossier_id) ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    onClick={() => toggleDossierSelection(d.dossier_id)}
                  >
                    <div className="flex items-center gap-3">
                      <input 
                        type="checkbox" 
                        checked={selectedDossiers.includes(d.dossier_id)}
                        onChange={() => {}}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{d.title}</p>
                        <p className="text-xs text-gray-500">{d.sector}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Si no seleccionas ninguno, se usarán los más recientes</p>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="generate_cover"
                checked={editionForm.generate_cover}
                onChange={(e) => setEditionForm({...editionForm, generate_cover: e.target.checked})}
              />
              <label htmlFor="generate_cover" className="text-sm">Generar portada con IA</label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateEdition(false)} className="flex-1" disabled={generating}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={generating}>
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</> : <><Plus className="w-4 h-4 mr-2" /> Crear Edición</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagementPage;
